/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import parse from "html-react-parser";

// Component impors
import { Button, Col, Descriptions, Grid, Row, Space, Table } from "antd";
import { Breakpoint } from "antd/lib";
import { IProject } from "@/interfaces/Project.interface";

// Misc imports
import { useGetSubscriptions, useUserInfo } from "@/api/user";
import { useWorkspacesList } from "@/api/workspaces";
import { CURRENCY_SYMBOLS, NAV_PROJECT_SINGLE_VIEW, NAV_SUBSCRIPTION_PLANS, PAGINATION_OPTIONS } from "@/constants";
import { getFormattedDate } from "@/utils/date/getFormattedDate";
import { numberToFixed } from "@/utils/number/numberToFixed";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { DataLoadingSkeleton } from "@/components/UI/DataLoadingSkeleton";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";

export const SubscriptionsList = () => {
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tokensInstance = Tokens;
  const userAuth:ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  const { data: userInfo } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);

  const isScreenBelowSM = () => screens.xs || (screens.sm && !screens.md) || false;
  const isScreenBelowLG = () => screens.xs || !screens.lg;

  const { data: workspaces } = useWorkspacesList();
  
  const { data: subscriptions, isLoading: isLoadingSubscriptions } = useGetSubscriptions(tokensInstance.getTokenSub(accessToken) as string);


  const [projects, setProjects] = useState<IProject[]>(
    workspaces ? workspaces.flatMap((workspace) => workspace.projects.map((project) => ({ ...project }))) : []
  );

  // Get WorkspaceId and ProjectId based on method param project
  const getProjectParams = (record: IProject): { workspaceId: string; projectId: string } => {
    const workspaceId = workspaces?.find((workspace) => workspace.projects.find((project) => project.uuid === record.uuid))?.uuid;
    return {
      workspaceId: workspaceId as string,
      projectId: record.uuid as string,
    };
  };

  useEffect(() => {
    if (!workspaces) {
      return;
    }
    const _projects = workspaces.flatMap((workspace) => workspace.projects.map((project) => ({ ...project })));

    setProjects(_projects);
  }, [workspaces]);

  // Project columns
  const columns = [
    {
      title: t("projects.list.project_title"),
      dataIndex: "title",
      key: "title",
      render: (_: any, record: IProject) => {
        const params = getProjectParams(record);
        return (
          <Button
            className="px-0"
            onClick={() =>
              navigate(
                `${replaceUrlParams(NAV_PROJECT_SINGLE_VIEW, {
                  ...params,
                })}`, { state: { canGoBack: true } }
              )
            }
            type="link"
          >
            {record.title}
          </Button>
        );
      },
    },
    {
      title: t("projects.list.subscription_plan"),
      dataIndex: "subscription_plan",
      key: "subscription_plan",
    },
    {
      title: t("projects.list.subscription_amount"),
      key: "subscription_amount",
      responsive: ["md"] as Breakpoint[],
      render: (_: string, record: IProject) => {
        const stripeData = subscriptions?.find((sub) => sub.project_uuid === record.uuid);

        if (!stripeData) {
          return "-";
        }
        return `${parse(CURRENCY_SYMBOLS[stripeData.currency].icon)} ${numberToFixed(stripeData.amount)}`;
      },
    },
    {
      title: t("projects.list.subscription_interval"),
      key: "subscription_interval",
      responsive: ["md"] as Breakpoint[],
      render: (_: string, record: IProject) => {
        const stripeData = subscriptions?.find((sub) => sub.project_uuid === record.uuid);

        if (!stripeData) {
          return "-";
        }
        return stripeData.billing_interval;
      },
    },
    {
      title: t("projects.list.subscription_billing_date"),
      key: "subscription_interval",
      responsive: ["lg"] as Breakpoint[],
      render: (_: string, record: IProject) => {
        const stripeData = subscriptions?.find((sub) => sub.project_uuid === record.uuid);

        if (!stripeData) {
          return "-";
        }
        return getFormattedDate(stripeData.billing_date, userInfo?.timezone);
      },
    },
    {
      title: t("projects.list.services"),
      key: "action",
      width: "150px",
      render: (_: any, record: IProject) => {
        const params = getProjectParams(record);
        return (
          <Space size="middle">
            {/* Link to Project's Cache Settings */}
            <Button
              onClick={() =>
                navigate(
                  `${replaceUrlParams(NAV_SUBSCRIPTION_PLANS, {
                    ...params,
                  })}`
                )
              }
              className="px-0"
              type="link"
            >
              {t("common.upgrade")}
            </Button>
            {/* ./ Link to Project's Cache Settings */}
          </Space>
        );
      },
    },
  ];

  if (!subscriptions && isLoadingSubscriptions) {
    return <DataLoadingSkeleton />;
  }

  return (
    <Row>
      <Col xs={24}>
        {/* Subscriptions List */}
        <Table
        size="small"
          bordered
          scroll={{ x: true }}
          rowKey="uuid"
          dataSource={projects}
          columns={columns}
          pagination={{ ...PAGINATION_OPTIONS }}
          expandable={{
            rowExpandable: () => isScreenBelowSM() || isScreenBelowLG(),
            showExpandColumn: isScreenBelowSM() || isScreenBelowLG(),
            expandedRowRender: (record) => {
              return (
                <Descriptions bordered size="small">
                  <Descriptions.Item span={3} label={t("projects.list.subscription_billing_date")}>
                    {subscriptions?.find((sub) => sub.project_uuid === record.uuid)?.billing_date
                      ? getFormattedDate(subscriptions?.find((sub) => sub.project_uuid === record.uuid)?.billing_date, userInfo?.timezone)
                      : "-"}
                  </Descriptions.Item>
                  {isScreenBelowSM() ? (
                    <>
                      <Descriptions.Item span={3} label={t("projects.list.subscription_interval")}>
                        {subscriptions?.find((sub) => sub.project_uuid === record.uuid)?.billing_interval ?? "-"}
                      </Descriptions.Item>
                      <Descriptions.Item span={3} label={t("projects.list.subscription_amount")}>
                        {subscriptions
                          ? `${parse(
                              CURRENCY_SYMBOLS[subscriptions.find((sub) => sub.project_uuid === record.uuid)?.currency ?? "eur"].icon
                            )} ${numberToFixed(subscriptions.find((sub) => sub.project_uuid === record.uuid)?.amount ?? 0)}`
                          : ""}
                      </Descriptions.Item>
                    </>
                  ) : null}
                </Descriptions>
              );
            },
          }}
        />
        {/* ./ Subscriptions List */}
      </Col>
    </Row>
  );
};
